/**
 * Calculates total hours, night hours, and holiday hours for a given date range.
 * 
 * @param startDate The start date of the contract (inclusive).
 * @param endDate The end date of the contract (inclusive).
 * @param scheduleHoursPerDay How many hours a post works per day (e.g., 24, 12).
 * @param holidays Array of strict 'MM-DD' strings that are considered holidays.
 */
export function calculateContractHours(
  startDate: Date,
  endDate: Date,
  scheduleHoursPerDay: number,
  holidays: string[]
) {
  // Normalize dates to midnight to count full days
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (end < start) {
    return {
      totalHours: 0,
      nightHours: 0,
      holidayHours: 0,
      daysCount: 0,
    };
  }

  // Number of days including both start and end dates
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

  // Let's assume daily hours spread evenly for simplicity of contract total calculation.
  const totalHours = diffDays * scheduleHoursPerDay;

  // Night hours calculation
  // Law: Night time is from 22:00 to 06:00 (8 hours per calendar day).
  // Per Order 45 and Labor Code art.96, night bonus applies to hours actually worked at night.
  // 
  // Strategy: calculate overlap between shift and night window [22:00-06:00].
  // - 24h shift: full 8h night
  // - 12h shift: depends on start hour, but typical охранные 12h shifts:
  //   Day 12h (08:00-20:00): 0 night hours
  //   Night 12h (20:00-08:00): 8h full night (22:00-06:00)
  // - For simplicity and conservative estimate: 
  //   shifts >= 12h: assume night shift scenario → 8 night hours
  //   shifts < 12h: assume day shift → 0 night hours
  let nightHoursPerDay = 0;
  if (scheduleHoursPerDay >= 24) {
    nightHoursPerDay = 8;  // full calendar day = full night
  } else if (scheduleHoursPerDay >= 12) {
    nightHoursPerDay = 8;  // 12h night shift (20:00-08:00) covers full night window
  } else if (scheduleHoursPerDay >= 8) {
    nightHoursPerDay = Math.min(scheduleHoursPerDay, 4);  // partial overlap estimate
  }
  // else: < 8h shift → 0 night hours

  const nightHoursTotal = diffDays * nightHoursPerDay;

  // Holiday calculation
  // We need to iterate over every day and check if it's a holiday in the list
  let holidayDaysCount = 0;
  
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const mmdd = `${month}-${day}`;
    
    if (holidays.includes(mmdd)) {
      holidayDaysCount++;
    }
  }

  const holidayHoursTotal = holidayDaysCount * scheduleHoursPerDay;

  return {
    totalHours,
    nightHours: nightHoursTotal,
    holidayHours: holidayHoursTotal,
    daysCount: diffDays,
  };
}
